import { useState } from "react";
import { api, type Me } from "../api";
import { useAsync } from "../hooks";
import { fmt } from "../format";
import { useConfirmAction } from "./useConfirmAction";

interface Operator {
  id: string;
  email: string;
  linked: boolean;
  createdAt: string;
}

/**
 * Operator identity (ROADMAP L3): grant/revoke the platform operator role on real accounts. The
 * everyday admin path — the shared ADMIN_API_KEY stays only as break-glass. Operator-only (403 at
 * the API for everyone else); reached behind `me.admin`.
 */
export function Users({ me }: { me: Me }) {
  const { data, loading, error, reload } = useAsync<Operator[]>(() => api("/operators"), []);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const confirmRevoke = useConfirmAction();

  async function grant() {
    const e = email.trim().toLowerCase();
    if (!e) return;
    setBusy("grant");
    setErr(null);
    try {
      await api("/operators", { method: "POST", body: JSON.stringify({ email: e }) });
      setEmail("");
      reload();
    } catch (x) {
      setErr(x instanceof Error ? x.message : "failed to grant");
    } finally {
      setBusy(null);
    }
  }

  function revoke(op: Operator) {
    confirmRevoke.ask({
      title: `Revoke operator access from ${op.email}?`,
      confirmLabel: "Revoke",
      danger: true,
      run: async () => {
        await api(`/operators/${op.id}`, { method: "DELETE" });
        reload();
      },
    });
  }

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  if (!data) return null;

  const operators = data;
  const last = operators.length <= 1;

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Users</h1>
        <span className="muted">Who can operate the platform. Operator only.</span>
      </div>

      <p className="note">
        An operator is Amendie staff — they see every client, the fleet, heals and settings. Access
        is a role on a real Google account, read fresh on every request, so a revoke takes effect
        immediately. The shared admin key still works as break-glass, but the everyday path is here.
      </p>

      <section className="card">
        <div className="section-head">
          <h2>Grant operator</h2>
        </div>
        <p className="note" style={{ marginTop: 6 }}>
          By email. If they have never signed in, the account is pre-created and waits for their
          first Google login (which is allowed even while sign-ups are paused).
        </p>
        {err && <div className="err mt">{err}</div>}
        <div className="row-gap" style={{ marginTop: "var(--sp4)", gap: "var(--sp3)" }}>
          <input
            type="email"
            placeholder="teammate@amendie.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void grant()}
            style={{ flex: 1, minWidth: 0 }}
          />
          <button className="btn seam" disabled={busy === "grant" || !email.trim()} onClick={() => void grant()}>
            {busy === "grant" ? "Granting…" : "Grant operator"}
          </button>
        </div>
      </section>

      <section className="card">
        <div className="section-head">
          <h2>Operators</h2>
          <span className="muted sm">{operators.length}</span>
        </div>
        <table style={{ marginTop: "var(--sp3)" }}>
          <thead>
            <tr>
              <th>Email</th>
              <th>Status</th>
              <th>Since</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {operators.map((op) => {
              const isSelf = op.id === me.userId;
              const disabled = busy === op.id || isSelf || last;
              const why = isSelf
                ? "You cannot revoke your own access"
                : last
                  ? "Cannot revoke the last operator"
                  : undefined;
              return (
                <tr key={op.id}>
                  <td>
                    {op.email}
                    {isSelf && <span className="muted sm"> (you)</span>}
                  </td>
                  <td>
                    <span className={op.linked ? "pill" : "pill hold"}>
                      {op.linked ? "active" : "pending first login"}
                    </span>
                  </td>
                  <td className="muted sm">{fmt(op.createdAt)}</td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      className="btn danger"
                      disabled={disabled}
                      title={why}
                      onClick={() => void revoke(op)}
                    >
                      {busy === op.id ? "…" : "Revoke"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      {confirmRevoke.modal}
    </div>
  );
}
